import { useState, useEffect } from 'react';
import { AlertTriangle, Shield, Clock, Users, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useSafetyStatus } from '@/hooks/useEmailBackend';
import type { Contact } from '@/types/contact';

interface SafetyValidationPanelProps {
  selectedContacts: Contact[];
  workspaceId: string;
  onValidationChange?: (isValid: boolean, warnings: SafetyWarning[]) => void;
}

interface SafetyWarning {
  type: 'quota' | 'rate_limit' | 'cooldown' | 'batch_size' | 'timeline' | 'recipient_reuse';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  suggestion?: string;
}

export function SafetyValidationPanel({ 
  selectedContacts, 
  workspaceId,
  onValidationChange 
}: SafetyValidationPanelProps) {
  const [validationWarnings, setValidationWarnings] = useState<SafetyWarning[]>([]);
  const [estimatedTimeline, setEstimatedTimeline] = useState<{
    startTime: Date;
    endTime: Date;
    totalHours: number;
    safetyTier: 'optimal' | 'caution' | 'restricted';
  } | null>(null);

  const { data: safetyStatus } = useSafetyStatus();
  
  // Validate campaign safety
  useEffect(() => {
    const warnings: SafetyWarning[] = [];
    
    // Check recipient count
    const recipientCount = selectedContacts.length;
    
    if (recipientCount === 0) {
      warnings.push({
        type: 'batch_size',
        severity: 'critical',
        message: 'No recipients selected',
        suggestion: 'Select at least one contact to create a campaign'
      });
    } else if (recipientCount > 10000) {
      warnings.push({
        type: 'batch_size',
        severity: 'high',
        message: `Large recipient list (${recipientCount.toLocaleString()} contacts)`,
        suggestion: 'Consider splitting into smaller campaigns for better deliverability'
      });
    } else if (recipientCount > 5000) {
      warnings.push({
        type: 'batch_size',
        severity: 'medium',
        message: `Medium recipient list (${recipientCount.toLocaleString()} contacts)`,
        suggestion: 'Campaign will use progressive scheduling for safety'
      });
    }

    // Estimate timeline based on recipient count
    let totalHours = 0;
    let safetyTier: 'optimal' | 'caution' | 'restricted' = 'optimal';
    
    if (recipientCount > 0) {
      if (recipientCount <= 1000) {
        // Optimal: 15-minute intervals
        totalHours = (recipientCount * 15) / 60; // 15 minutes per email
        safetyTier = 'optimal';
      } else if (recipientCount <= 5000) {
        // Caution: 30-minute intervals
        totalHours = (recipientCount * 30) / 60;
        safetyTier = 'caution';
        warnings.push({
          type: 'timeline',
          severity: 'medium',
          message: `Extended timeline required (${Math.ceil(totalHours)} hours)`,
          suggestion: 'Campaign will use 30-minute intervals for safety'
        });
      } else {
        // Restricted: 60-minute intervals
        totalHours = (recipientCount * 60) / 60;
        safetyTier = 'restricted';
        warnings.push({
          type: 'timeline',
          severity: 'high',
          message: `Long timeline required (${Math.ceil(totalHours)} hours)`,
          suggestion: 'Campaign will use 60-minute intervals for maximum safety'
        });
      }

      const startTime = new Date();
      const endTime = new Date(startTime.getTime() + totalHours * 60 * 60 * 1000);
      
      setEstimatedTimeline({
        startTime,
        endTime,
        totalHours,
        safetyTier
      });
    }

    // Check for active safety controls
    if (safetyStatus?.data?.active_controls?.length) {
      safetyStatus.data.active_controls.forEach(control => {
        if (control.type === 'global_pause') {
          warnings.push({
            type: 'rate_limit',
            severity: 'critical',
            message: 'Global email pause is active',
            suggestion: 'Contact administrator to resolve before sending'
          });
        } else if (control.type === 'workspace_pause' && control.target_id === workspaceId) {
          warnings.push({
            type: 'rate_limit',
            severity: 'critical',
            message: 'Workspace email pause is active',
            suggestion: 'Contact administrator to resolve before sending'
          });
        }
      });
    }

    // Check system health
    if (safetyStatus?.data?.system_status === 'degraded') {
      warnings.push({
        type: 'rate_limit',
        severity: 'medium',
        message: 'Email system is experiencing issues',
        suggestion: 'Delays may occur during processing'
      });
    } else if (safetyStatus?.data?.system_status === 'maintenance') {
      warnings.push({
        type: 'rate_limit',
        severity: 'high',
        message: 'Email system is under maintenance',
        suggestion: 'Consider scheduling for later'
      });
    }

    setValidationWarnings(warnings);
    
    // Notify parent component
    const isValid = !warnings.some(w => w.severity === 'critical');
    onValidationChange?.(isValid, warnings);
    
  }, [selectedContacts, workspaceId, safetyStatus, onValidationChange]);

  const getSeverityColor = (severity: SafetyWarning['severity']) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: SafetyWarning['severity']) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="h-4 w-4" />;
      case 'high': return <AlertTriangle className="h-4 w-4" />;
      case 'medium': return <AlertTriangle className="h-4 w-4" />;
      case 'low': return <AlertCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getTierColor = (tier: 'optimal' | 'caution' | 'restricted') => {
    switch (tier) {
      case 'optimal': return 'bg-green-500';
      case 'caution': return 'bg-yellow-500'; 
      case 'restricted': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const hasWarnings = validationWarnings.length > 0;

  return (
    <div className="space-y-4">
      {/* Safety Status Header */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Shield className="h-4 w-4" />
            Campaign Safety Validation
            {!hasWarnings && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                All Clear
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Recipients Summary */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              {selectedContacts.length.toLocaleString()} recipients selected
            </div>
            {estimatedTimeline && (
              <Badge 
                variant="outline" 
                className={`${getTierColor(estimatedTimeline.safetyTier)} text-white border-none`}
              >
                {(estimatedTimeline.safetyTier || 'normal').charAt(0).toUpperCase() + (estimatedTimeline.safetyTier || 'normal').slice(1)} Schedule
              </Badge>
            )}
          </div>

          {/* Timeline Estimate */}
          {estimatedTimeline && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                Estimated completion: {Math.ceil(estimatedTimeline.totalHours)} hours
              </div>
              <Progress 
                value={0} 
                className="h-2"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Start: {estimatedTimeline.startTime.toLocaleTimeString()}</span>
                <span>End: {estimatedTimeline.endTime.toLocaleTimeString()}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Validation Warnings */}
      {hasWarnings && (
        <div className="space-y-2">
          {validationWarnings.map((warning, index) => (
            <Alert 
              key={index}
              variant={warning.severity === 'critical' || warning.severity === 'high' ? 'destructive' : 'default'}
            >
              {getSeverityIcon(warning.severity)}
              <AlertDescription className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{warning.message}</span>
                  <Badge variant={getSeverityColor(warning.severity)} className="text-xs">
                    {warning.severity.toUpperCase()}
                  </Badge>
                </div>
                {warning.suggestion && (
                  <div className="text-sm text-muted-foreground">
                    💡 {warning.suggestion}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* System Status */}
      {safetyStatus?.data && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Email System Status:</span>
              <Badge 
                variant={safetyStatus.data.system_status === 'operational' ? 'secondary' : 'destructive'}
                className={safetyStatus.data.system_status === 'operational' ? 'bg-green-100 text-green-800' : ''}
              >
                {(safetyStatus.data.system_status || 'unknown').charAt(0).toUpperCase() + (safetyStatus.data.system_status || 'unknown').slice(1)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}